import { useState } from "react";
import { Check, Eye, EyeOff, X } from "lucide-react";

export const inputCls =
  "w-full rounded-xl border border-neutral-300 bg-white px-3.5 py-2.5 text-sm text-[#111111] outline-none transition-colors placeholder:text-neutral-400 focus:border-[#111111]";

export const inputClsError =
  "w-full rounded-xl border border-red-500 bg-white px-3.5 py-2.5 text-sm text-[#111111] outline-none transition-colors placeholder:text-neutral-400 focus:border-red-600";

export function PageHeader({ title, sub, children }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="text-2xl font-extrabold tracking-tight text-[#111111]">{title}</h1>
        {sub && <p className="mt-1 max-w-2xl text-sm leading-relaxed text-neutral-500">{sub}</p>}
      </div>
      {children}
    </div>
  );
}

export function Card({ className = "", children, ...props }) {
  return (
    <div className={`rounded-2xl border border-neutral-200 bg-white ${className}`} {...props}>
      {children}
    </div>
  );
}

export function Modal({ title, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4" onClick={onClose}>
      <div
        data-testid={testid}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-neutral-200 bg-white p-6 shadow-2xl"
        style={{ scrollPaddingBlock: "45vh" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#111111]">{title}</h2>
          <button
            data-testid="modal-close-button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-8 w-8 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-[#111111]"
          >
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, hint, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-semibold text-neutral-600">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-neutral-400">{hint}</p>}
    </div>
  );
}

export function Btn({ variant = "primary", className = "", ...props }) {
  const styles =
    variant === "primary"
      ? "bg-[#111111] text-white hover:bg-neutral-800"
      : variant === "danger"
        ? "border border-neutral-300 text-red-600 hover:bg-red-50"
        : "border border-neutral-300 bg-white text-[#111111] hover:bg-neutral-50";
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors disabled:opacity-50 ${styles} ${className}`}
      {...props}
    />
  );
}

const TONES = {
  black: "bg-[#111111] text-white",
  grey: "bg-neutral-100 text-neutral-600",
  outline: "border border-neutral-300 text-neutral-500",
  red: "bg-red-50 text-red-600",
};

export function Pill({ children, tone = "grey" }) {
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${TONES[tone]}`}>
      {children}
    </span>
  );
}

export function Empty({ icon: Icon, title, body, testid }) {
  return (
    <div data-testid={testid} className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-neutral-300 bg-neutral-50/50 px-6 py-14 text-center">
      {Icon && (
        <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-neutral-400 shadow-sm ring-1 ring-neutral-200">
          <Icon size={22} />
        </span>
      )}
      <p className="text-sm font-bold text-[#111111]">{title}</p>
      {body && <p className="mt-1.5 max-w-sm text-xs leading-relaxed text-neutral-500">{body}</p>}
    </div>
  );
}

export function Notice({ title, body, testid }) {
  return (
    <div data-testid={testid} className="rounded-2xl border border-neutral-200 bg-neutral-50 px-5 py-4">
      <p className="text-sm font-bold text-[#111111]">{title}</p>
      <p className="mt-1 text-xs leading-relaxed text-neutral-500">{body}</p>
    </div>
  );
}

export function PasswordField({ testid, value, onChange, placeholder, invalid, inputId }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        data-testid={testid}
        id={inputId}
        className={`${invalid ? inputClsError : inputCls} pr-11`}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        aria-invalid={invalid || undefined}
        required
      />
      <button
        type="button"
        data-testid={`${testid}-toggle`}
        aria-label={show ? "Hide password" : "Show password"}
        onClick={() => setShow((v) => !v)}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 transition-colors hover:text-[#111111]"
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

export const passwordRules = [
  { label: "At least 14 characters", test: (p) => p.length >= 14 },
  { label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { label: "One number", test: (p) => /\d/.test(p) },
  { label: "One special symbol", test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export function PasswordRules({ password }) {
  return (
    <ul className="space-y-1.5 rounded-xl border border-neutral-200 bg-neutral-50/60 p-3.5" data-testid="password-rules">
      {passwordRules.map((r) => {
        const ok = r.test(password);
        return (
          <li key={r.label} className={`flex items-center gap-2 text-xs ${ok ? "font-medium text-[#111111]" : "text-neutral-400"}`}>
            <span className={`flex h-4 w-4 items-center justify-center rounded-full ${ok ? "bg-[#111111] text-white" : "border border-neutral-300"}`}>
              {ok && <Check size={10} strokeWidth={3.5} />}
            </span>
            {r.label}
          </li>
        );
      })}
    </ul>
  );
}
